import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Student from 'teacher-ui-client/models/student'
import { buildDataOperationsConfigProxy } from 'teacher-ui-client/components/data-operations/data-operations-proxy'
import { t, type IntlService } from 'ember-intl'
import { SortDirection, SortType } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import type { Task } from 'ember-concurrency'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { DataOperationsTable } from 'teacher-ui-client/components/data-operations/table'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import FidgetLoadingIndicator from '@blakeelearning/fidget/components/fidget/loading-indicator'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { eq } from 'ember-truth-helpers'

interface Args {
  studentsTask: Task<Student[], [params: unknown]>
  subscriptionType: SubscriptionType
}

interface Signature {
  Element: HTMLTableElement
  Args: Args
}

export class BookLibraryShareStudentsTable extends Component<Signature> {
  @service intl!: IntlService

  @service gradeSets!: GradeSetsService

  getSchoolClassNamesForStudent(student: Student) {
    return student.readingSchoolClassNames.join(', ')
  }

  get data() {
    return (
      this.args.studentsTask.last?.value?.map((student: Student, index: number) => ({
        id: index,
        firstName: student.firstName,
        lastName: student.lastName,
        schoolClassNames: this.getSchoolClassNamesForStudent(student),
        grade: this.gradeSets.nameFromGradeSetByPosition(student.gradePosition),
        gradePosition: student.gradePosition,
      })) ?? []
    )
  }

  get dataOperationsConfig() {
    return buildDataOperationsConfigProxy(this)
  }

  @tracked sortCol = 'firstName'

  @tracked sortDir = SortDirection.Asc

  @tracked currentPage = 1

  @tracked recordsPerPage = 6

  get columns() {
    return [
      {
        label: this.intl.t('manage.firstName'),
        valuePath: 'firstName',
        sortable: SortType.ByValue,
        classNames: 'w-3/12',
      },
      {
        label: this.intl.t('manage.lastName'),
        valuePath: 'lastName',
        sortable: SortType.ByValue,
        classNames: 'w-3/12',
      },
      {
        label: this.intl.t('manage.schoolClasses.namePlural'),
        valuePath: `schoolClassNames`,
        sortable: SortType.ByValue,
        classNames: 'w-4/12',
      },
      {
        label: this.intl.t('grade.label'),
        valuePath: 'gradePosition',
        sortable: SortType.ByGrade,
        classNames: 'w-2/12',
      },
    ]
  }

  <template>
    {{#if @studentsTask.isRunning}}
      <FidgetLoadingIndicator
        data-test-share-students-loading
        class="my-2"
        @show={{true}}
        @centered={{true}}
        @appearance="small"
        @overlay={{false}}
      />
    {{else}}
      <DataOperationsTable @dataOperationsConfig={{this.dataOperationsConfig}} as |_selectRecords sorting pagination|>
        <table ...attributes class="mb-2 w-full table-fixed bg-white">
          <thead>
            <tr>
              {{#each this.columns as |col|}}
                <th
                  role="button"
                  data-test-col={{col.valuePath}}
                  class="{{col.classNames}} text-dusty-black-250 px-4 py-2 font-normal uppercase select-none"
                  {{on "click" (fn sorting.sort col.sortable col.valuePath)}}
                >
                  {{col.label}}
                  <PhosphorIcon
                    @icon={{if
                      (eq sorting.sortCol col.valuePath)
                      (if (eq sorting.sortDir "asc") "phosphor-caret-up-fill" "phosphor-caret-down-fill")
                      "phosphor-caret-up-down-fill"
                    }}
                    role="presentation"
                  />
                </th>
              {{/each}}
            </tr>
          </thead>
          <tbody>
            {{#each pagination.currentPageRecords as |row index|}}
              <tr data-test-record-row={{index}} class="py-3">
                <td class="truncate px-4 py-2" data-test-cell="firstName">
                  {{row.firstName}}
                </td>
                <td class="truncate px-4 py-2" data-test-cell="lastName">
                  {{row.lastName}}
                </td>
                <td class="truncate px-4 py-2" data-test-cell="schoolClassNames">
                  {{row.schoolClassNames}}
                </td>
                <td class="px-4 py-2" data-test-cell="gradePosition">
                  {{row.grade}}
                </td>
              </tr>
            {{else}}
              <tr data-test-no-records-row class="py-3">
                <td class="py-4" colspan="4">
                  <NoResultsMessage
                    @compact={{true}}
                    @body={{t "bookLibrary.components.shareCollectionTable.emptyMessage"}}
                  />
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      </DataOperationsTable>
    {{/if}}
  </template>
}

export default BookLibraryShareStudentsTable
